import AccountCombobox from "@app/components/account-combobox";
import type { AccountOption } from "@app/components/account-combobox";
import StakeholderCombobox from "@app/components/stakeholder-combobox";
import type { StakeholderOption } from "@app/components/stakeholder-combobox";
import { useToast } from "@app/components/toast";
import TxnTablePanel, {
  BulkAction,
  TXN_PANEL_DIALOG_CLASS,
  useTxnSort,
} from "@app/components/txn-table-panel";
import { useTxnPanelRefresh } from "@app/components/txn-table-panel/use-txn-panel-refresh";
import { getPerspectiveId } from "@app/lib/perspective";
import { environment } from "@app/relay/relay-environment";
import * as Dialog from "@radix-ui/react-dialog";
import { Suspense, useState } from "react";
import { commitMutation, graphql, useLazyLoadQuery } from "react-relay";

import type { revenueCellPanel_Query } from "@app/queries/revenueCellPanel_Query.graphql";
import type { revenueCellPanel_MarkTxnsInternalMutation } from "@app/queries/revenueCellPanel_MarkTxnsInternalMutation.graphql";
import type { revenueCellPanel_AttributeTxnsMutation } from "@app/queries/revenueCellPanel_AttributeTxnsMutation.graphql";
import type { revenueCellPanel_ClassifyTxnsMutation } from "@app/queries/revenueCellPanel_ClassifyTxnsMutation.graphql";
import type { CellScope } from "./revenue-grid";

const cellQuery = graphql`
  query revenueCellPanel_Query(
    $accountId: ID
    $month: String
    $from: String
    $to: String
    $perspectiveId: ID
    $sort: TxnSortEnum
    $sortDirection: String
  ) {
    transactions(
      flow: REVENUE
      excludeDistributions: true
      accountId: $accountId
      month: $month
      from: $from
      to: $to
      perspectiveId: $perspectiveId
      sort: $sort
      sortDirection: $sortDirection
    ) {
      totalCount
      totalAmount
      limit
      txns {
        txn {
          id
        }
        md5
        postingDate
        description
        amount
        kind
        accountNumber
        internal
        classification
        expenseNature {
          id
          name
        }
        counterparty {
          name
        }
        category
      }
    }
  }
`;

const markTxnsInternalMutation = graphql`
  mutation revenueCellPanel_MarkTxnsInternalMutation($input: MarkTxnsInternalInputObject!) {
    markTxnsInternal(input: $input) {
      updatedCount
      errors {
        message
      }
    }
  }
`;

const attributeTxnsMutation = graphql`
  mutation revenueCellPanel_AttributeTxnsMutation($input: AttributeTxnsInputObject!) {
    attributeTxns(input: $input) {
      stakeholder {
        id
        name
      }
      errors {
        message
      }
    }
  }
`;

const classifyTxnsMutation = graphql`
  mutation revenueCellPanel_ClassifyTxnsMutation($input: ClassifyTxnsInputObject!) {
    classifyTxns(input: $input) {
      updatedCount
      errors {
        message
      }
    }
  }
`;

// The variables that scope the transaction list to exactly the clicked cell.
function variablesFor(scope: CellScope, year: number) {
  const from = `${year}-01-01`;
  const to = `${year}-12-31`;
  switch (scope.kind) {
    case "account-month":
      return { accountId: scope.accountId, month: scope.month, from: undefined, to: undefined };
    case "account-year":
      return { accountId: scope.accountId, month: undefined, from, to };
    case "all-month":
      return { accountId: undefined, month: scope.month, from: undefined, to: undefined };
    case "all-year":
      return { accountId: undefined, month: undefined, from, to };
  }
}

// The cell drill-down: the revenue transactions that sum to the clicked amount,
// reviewable and fixable in place. "Not revenue" marks a txn internal (it leaves
// the corpus); "Set counterparty" attributes it. Both re-derive server-side
// (Pipeline::Delta) and refresh the grid via onChanged.
export default function RevenueCellPanel({
  scope,
  year,
  accountOptions,
  stakeholderOptions,
  onClose,
  onChanged,
}: {
  scope: CellScope;
  year: number;
  accountOptions: readonly AccountOption[];
  stakeholderOptions: readonly StakeholderOption[];
  onClose: () => void;
  onChanged: () => void;
}) {
  return (
    <Dialog.Root open onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/20 z-40" />
        <Dialog.Content className={TXN_PANEL_DIALOG_CLASS}>
          <Suspense fallback={<div className="p-6 text-sm text-gray-400">Loading transactions…</div>}>
            <PanelContent
              scope={scope}
              year={year}
              accountOptions={accountOptions}
              stakeholderOptions={stakeholderOptions}
              onClose={onClose}
              onChanged={onChanged}
            />
          </Suspense>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function PanelContent({
  scope,
  year,
  accountOptions,
  stakeholderOptions,
  onClose,
  onChanged,
}: {
  scope: CellScope;
  year: number;
  accountOptions: readonly AccountOption[];
  stakeholderOptions: readonly StakeholderOption[];
  onClose: () => void;
  onChanged: () => void;
}) {
  const notify = useToast();
  const { refreshing, refresh, queryOptions } = useTxnPanelRefresh();
  const [busy, setBusy] = useState(false);
  const { sort, direction, onSort, sorting, sortVars } = useTxnSort();

  const vars = variablesFor(scope, year);
  const data = useLazyLoadQuery<revenueCellPanel_Query>(
    cellQuery,
    { ...vars, perspectiveId: getPerspectiveId() ?? undefined, ...sortVars },
    queryOptions
  );
  const rows = data.transactions.txns;

  // The mutations re-derive downstream (Pipeline::Delta) server-side, so once they
  // complete the grid cell and this list both need fresh data.
  const afterMutation = (clearSelection: () => void) => {
    clearSelection();
    setBusy(false);
    refresh();
    onChanged();
  };

  const fail = (message: string) => {
    setBusy(false);
    notify({ message, kind: "error" });
  };

  const markInternal = (txnIds: string[], clearSelection: () => void, choice: { bankAccountId?: string; name?: string }) => {
    setBusy(true);
    commitMutation<revenueCellPanel_MarkTxnsInternalMutation>(environment, {
      mutation: markTxnsInternalMutation,
      variables: {
        input: { txnIds, destinationBankAccountId: choice.bankAccountId, destinationName: choice.name },
      },
      onCompleted: (res) => {
        const payload = res.markTxnsInternal;
        if (payload?.errors?.length) return fail(payload.errors[0].message);
        notify({ message: `Removed ${txnIds.length} from revenue (marked internal).`, kind: "success" });
        afterMutation(clearSelection);
      },
      onError: () => fail("Could not mark internal — please try again."),
    });
  };

  const attribute = (txnIds: string[], clearSelection: () => void, choice: { stakeholderId?: string; name?: string }) => {
    setBusy(true);
    commitMutation<revenueCellPanel_AttributeTxnsMutation>(environment, {
      mutation: attributeTxnsMutation,
      variables: {
        input: {
          txnIds,
          stakeholderId: choice.stakeholderId,
          name: choice.name,
          perspectiveId: getPerspectiveId() ?? undefined,
        },
      },
      onCompleted: (res) => {
        const payload = res.attributeTxns;
        if (payload?.errors?.length) return fail(payload.errors[0].message);
        notify({ message: `Set the counterparty on ${txnIds.length} transaction(s).`, kind: "success" });
        afterMutation(clearSelection);
      },
      onError: () => fail("Could not attribute — please try again."),
    });
  };

  // It's a loan, not revenue: classify LOAN_PROCEEDS (debt taken on — real cash in,
  // offset by debt, excluded from operating revenue). Re-derives server-side.
  const markLoan = (txnIds: string[], clearSelection: () => void) => {
    setBusy(true);
    commitMutation<revenueCellPanel_ClassifyTxnsMutation>(environment, {
      mutation: classifyTxnsMutation,
      variables: { input: { txnIds, classification: "LOAN_PROCEEDS" } },
      onCompleted: (res) => {
        const payload = res.classifyTxns;
        if (payload?.errors?.length) return fail(payload.errors[0].message);
        notify({ message: `Marked ${txnIds.length} as loan proceeds (debt, not revenue).`, kind: "success" });
        afterMutation(clearSelection);
      },
      onError: () => fail("Could not mark as loan — please try again."),
    });
  };

  const exportParams: Record<string, string> = {
    flow: "REVENUE",
    exclude_distributions: "true",
    sort: sortVars.sort,
    sort_direction: sortVars.sortDirection,
  };
  if (vars.accountId) exportParams.account_id = vars.accountId;
  if (vars.month) exportParams.month = vars.month;
  if (vars.from) exportParams.from = vars.from;
  if (vars.to) exportParams.to = vars.to;
  const perspectiveId = getPerspectiveId();
  if (perspectiveId) exportParams.perspective_id = perspectiveId;

  return (
    <TxnTablePanel
      title={scope.label}
      totalCount={data.transactions.totalCount}
      headerTotalDollars={data.transactions.totalAmount}
      rows={rows}
      emptyLabel="No revenue transactions in this cell."
      sort={sort}
      direction={direction}
      onSort={onSort} sorting={sorting}
      exportParams={exportParams}
      busy={busy || refreshing}
      onClose={onClose}
      renderBulkActions={(selectedIds, clearSelection) => (
        <>
          <BulkAction label="Not revenue → mark internal" disabled={busy}>
            {(close) => (
              <AccountCombobox
                options={accountOptions}
                placeholder="The owned account on the other end…"
                onPick={(choice) => {
                  close();
                  markInternal(selectedIds, clearSelection, choice);
                }}
              />
            )}
          </BulkAction>
          <BulkAction label="Set counterparty" disabled={busy}>
            {(close) => (
              <StakeholderCombobox
                options={stakeholderOptions}
                onPick={(choice) => {
                  close();
                  attribute(selectedIds, clearSelection, choice);
                }}
              />
            )}
          </BulkAction>
          <button
            type="button"
            disabled={busy}
            onClick={() => markLoan(selectedIds, clearSelection)}
            title="It's borrowed money, not revenue — classify as loan proceeds (debt)"
            className="px-2 py-1 rounded bg-white text-gray-900 hover:bg-gray-100 text-xs disabled:opacity-50"
          >
            It&apos;s a loan
          </button>
        </>
      )}
    />
  );
}
